import { CalendarPlus, ClipboardList } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { ClientJobCard } from '@/components/client/ClientJobCard';
import { ClientPage } from '@/components/client/ClientPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { useClientJobs } from '@/hooks/client-portal';
import { JOB_STATUS_LABELS, type JobStatusValue } from '@shared/schemas/job';
import { DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * /portal/cleans — ROADMAP.md §5.2.
 *
 * **A card list, not a table, at every width.** That is the pattern FINDINGS.md
 * §10.1 says should replace the admin side's sideways-scrolling tables (P6) —
 * and it is a card at 1280 as much as at 375, not a table that stacks. The
 * DataTable's below-768 stacking is the admin fix for the admin problem; this
 * portal never had that problem and should not inherit its solution.
 *
 * Filter pills and a search box above, exactly as the admin jobs list has them,
 * because that part the prototype and the rebuild agree on.
 */

const STATUS_FILTERS: { value: JobStatusValue | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'scheduled', label: JOB_STATUS_LABELS.scheduled },
  { value: 'in_progress', label: JOB_STATUS_LABELS.in_progress },
  { value: 'completed', label: JOB_STATUS_LABELS.completed },
  { value: 'cancelled', label: JOB_STATUS_LABELS.cancelled },
];

export function MyCleansPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<JobStatusValue | 'all'>('all');
  const [page, setPage] = useState(1);

  const jobs = useClientJobs({ page, search, status });
  const filtered = search !== '' || status !== 'all';

  return (
    <ClientPage
      title="My Cleans"
      description="Every clean we have booked or done for you, newest first."
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label="Search your cleans"
          placeholder="Job number, address or service"
          className="sm:w-72"
        />

        <FilterPills
          label="Filter your cleans by status"
          options={STATUS_FILTERS}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />
      </div>

      {/*
        Three branches, and they are different things — the P22 rule. A failed
        request says so and offers to retry; it never renders as "you have no
        cleans", which is a sentence somebody would believe.
      */}
      {jobs.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load your cleans"
            hint="Something went wrong on our side. Try again — and ring the office on (435) 238-3286 if it keeps happening."
            onRetry={() => {
              void jobs.refetch();
            }}
          />
        </div>
      ) : jobs.isPending ? (
        <ul aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading your cleans</span>
          {[0, 1, 2].map((key) => (
            <li
              key={key}
              aria-hidden="true"
              className="h-[164px] animate-pulse rounded-lg bg-ink-100"
            />
          ))}
        </ul>
      ) : jobs.data && jobs.data.data.length > 0 ? (
        <ul className="space-y-3">
          {jobs.data.data.map((job) => (
            <ClientJobCard key={job.id} job={job} />
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={filtered ? ClipboardList : CalendarPlus}
            title={filtered ? 'Nothing matches those filters.' : 'No cleans yet.'}
            hint={
              filtered
                ? 'Try clearing the search, or showing all statuses.'
                : 'Once we have cleaned for you, every visit shows up here with its checklist and photos.'
            }
            action={
              filtered ? null : (
                <Button asChild>
                  <Link to="/portal/request">
                    <CalendarPlus aria-hidden="true" />
                    Request a Cleaning
                  </Link>
                </Button>
              )
            }
          />
        </div>
      )}

      {jobs.data ? (
        <Pagination
          page={jobs.data.page}
          pageSize={jobs.data.pageSize || DEFAULT_PAGE_SIZE}
          total={jobs.data.total}
          onPageChange={setPage}
          itemNoun="cleans"
        />
      ) : null}
    </ClientPage>
  );
}
